import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { IPersonalPracticeSim } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-simulation';
import { PersonalizedPracticeService } from 'src/app/services/personalized-practice/personalized-practice.service';
import { QaiInnerWrapComponent, QaiReportingCardComponent } from '@qai/ui';
import { PersonalizedPracticeHeaderComponent } from './personalized-practice-header/personalized-practice-header.component';
import { ITeam } from 'src/app/interfaces/reportingV2/iteams';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { TranslationService } from 'src/app/services/translation.service';
import { PersonalizedPracticeSessionsComponent } from './personalized-practice-sessions/personalized-practice-sessions.component';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { getAccountNameByUuid } from 'src/app/utils/local-storage.util';
import { IPersonalizedPracticeCallSummaryRequest } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-call-summary-request';
import { IPersonalizedPracticeCallSummaryResponse } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-call-summary-response';
import { IPersonalizedPracticeSessionReport } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-session-report';

@Component({
  selector: 'app-personalized-practice-details',
  templateUrl: './personalized-practice-details.component.html',
  styleUrl: './personalized-practice-details.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    QaiInnerWrapComponent,
    QaiReportingCardComponent,
    PersonalizedPracticeHeaderComponent,
    PersonalizedPracticeSessionsComponent,
  ],
})
export class PersonalizedPracticeDetailsComponent implements OnInit {
  simulation: IPersonalPracticeSim | null = null;
  heading: string = 'Personalized Practice';
  teamOptions: ITeam[] = [];
  selectedTeams: string[] = [];
  isLoadingTeams: boolean = false;
  isTranslationLoaded: boolean = false;
  callSummaryData: IPersonalizedPracticeCallSummaryResponse | null = null;
  isLoadingCallSummary: boolean = false;

  // sessions
  sessions: IPersonalizedPracticeSessionReport[] = [];
  isLoadingSessions: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private personalizedPracticeService: PersonalizedPracticeService,
    private reportingDashboardService: ReportingDashboardService,
    private translationService: TranslationService,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit(): void {
    this.loadSimulation();
    this.loadTeams();
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
    });
  }

  private loadSimulation(): void {
    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
    if (!accountUuid) {
      console.warn(
        'No account UUID available to load personalized practice modules'
      );
      return;
    }

    this.reportingDashboardService
      .getPersonalizedPracticeModules({
        accountUuid,
        teamUuids: [],
      })
      .subscribe({
        next: (simulations: IPersonalPracticeSim[]) => {
          const moduleId = this.route.snapshot.paramMap.get('id');
          this.simulation =
            simulations.find((s) => s.moduleUuid === moduleId) || null;
          this.heading = this.simulation?.moduleName || '';
          if (this.simulation) {
            this.loadPersonalizedPracticeCallSummaryData();
            this.trackPageView();
          }
        },
        error: (error) => {
          console.error('Error loading personalized practice modules:', error);
        },
      });
  }

  private trackPageView(): void {
    if (!this.simulation) {
      return;
    }

    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
    const accountName = accountUuid ? getAccountNameByUuid(accountUuid) : '';

    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.PERSONALIZED_PRACTICE_REPORTING_PAGE_PAGEVIEW,
      {
        product_name: this.simulation.moduleName,
        account_name: accountName,
      }
    );
  }

  private loadTeams() {
    this.isLoadingTeams = true;
    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();

    if (!accountUuid) {
      console.warn('Missing required data for user engagement API call');
      return;
    }

    this.reportingDashboardService.getTeams(accountUuid).subscribe({
      next: (teams) => {
        this.teamOptions = teams;
        this.isLoadingTeams = false;
      },
      error: (error) => {
        console.error('Error loading teams:', error);
        this.isLoadingTeams = false;
      },
    });
  }

  onTeamChange(teams: string[]) {
    this.selectedTeams = teams.filter((teamUuid) => teamUuid !== 'all');
    this.loadPersonalizedPracticeCallSummaryData();
  }

  private loadPersonalizedPracticeCallSummaryData() {
    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();

    if (!accountUuid || !this.simulation?.moduleUuid) {
      console.warn(
        'Missing required data for personalized practice call summary API call'
      );
      return;
    }

    this.isLoadingCallSummary = true;
    const request: IPersonalizedPracticeCallSummaryRequest = {
      teamUuids: this.selectedTeams,
      accountUuid: accountUuid,
      moduleUuid: this.simulation.moduleUuid,
    };

    this.personalizedPracticeService
      .getPersonalizedPracticeCallSummaryData(request)
      .subscribe({
        next: (response) => {
          this.callSummaryData = response;
          this.isLoadingCallSummary = false;
        },
        error: (error) => {
          console.error(
            'Error loading personalized practice call summary data:',
            error
          );
          this.isLoadingCallSummary = false;
        },
      });
  }

  formatTimeInMinutes(minutes: number | null | undefined): string {
    if (minutes == null) {
      return '--';
    }
    if (minutes >= 60) {
      const hours = Math.round((minutes / 60) * 10) / 10;
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
}
